import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { queryClient } from "@/lib/queryClient";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { hasAdminSession } from "@/lib/adminSession";

// Layouts
import PublicLayout from "@/components/layout/PublicLayout";
import AdminLayout from "@/components/admin/AdminLayout";

// Public pages
import HomePage from "@/pages/public/HomePage";
import AboutPage from "@/pages/public/AboutPage";
import OurWorkPage from "@/pages/public/OurWorkPage";
import ForumPage from "@/pages/public/ForumPage";
import EventsPage from "@/pages/public/EventsPage";
import EventDetailPage from "@/pages/public/EventDetailPage";
import UnitsPage from "@/pages/public/UnitsPage";
import UnitDetailPage from "@/pages/public/UnitDetailPage";
import SponsorsPage from "@/pages/public/SponsorsPage";
import MembershipSearchPage from "@/pages/public/MembershipSearchPage";
import { SHOW_ANNUAL_ACCOUNTS_PAGE } from "@/lib/siteFeatures";
import AnnualAccountsPage from "@/pages/public/AnnualAccountsPage";
import ContactPage from "@/pages/public/ContactPage";
import PrivacyPolicyPage from "@/pages/public/PrivacyPolicyPage";
import NotFoundPage from "@/pages/public/NotFoundPage";

// Admin pages
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminHomeSectionsPage from "@/pages/admin/AdminHomeSectionsPage";
import AdminForumsPage from "@/pages/admin/AdminForumsPage";
import AdminEventsPage from "@/pages/admin/AdminEventsPage";
import AdminUnitsPage from "@/pages/admin/AdminUnitsPage";
import AdminSponsorsPage from "@/pages/admin/AdminSponsorsPage";
import AdminBannersPage from "@/pages/admin/AdminBannersPage";
import AdminFormsPage from "@/pages/admin/AdminFormsPage";
import AdminSubmissionsPage from "@/pages/admin/AdminSubmissionsPage";
import AdminMembersPage from "@/pages/admin/AdminMembersPage";
import AdminMediaPage from "@/pages/admin/AdminMediaPage";
import AdminSettingsPage from "@/pages/admin/AdminSettingsPage";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="h-8 w-8 rounded-full border-2 border-maroon border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user || !hasAdminSession()) return <Navigate to="/admin/login" replace />;
  return children;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ScrollToTop />
        <Toaster position="top-right" richColors />
        <Routes>
          {/* Public site */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/our-work" element={<OurWorkPage />} />
            <Route path="/our-work/:slug" element={<ForumPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/:slug" element={<EventDetailPage />} />
            <Route path="/units" element={<UnitsPage />} />
            <Route path="/units/:slug" element={<UnitDetailPage />} />
            <Route path="/sponsors" element={<SponsorsPage />} />
            <Route path="/membership" element={<MembershipSearchPage />} />
            {SHOW_ANNUAL_ACCOUNTS_PAGE ? (
              <Route path="/annual-accounts" element={<AnnualAccountsPage />} />
            ) : (
              <Route path="/annual-accounts" element={<NotFoundPage />} />
            )}
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          {/* Admin */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboardPage />} />
            <Route path="home-sections" element={<AdminHomeSectionsPage />} />
            <Route path="forums" element={<AdminForumsPage />} />
            <Route path="events" element={<AdminEventsPage />} />
            <Route path="units" element={<AdminUnitsPage />} />
            <Route path="sponsors" element={<AdminSponsorsPage />} />
            <Route path="banners" element={<AdminBannersPage />} />
            <Route path="forms" element={<AdminFormsPage />} />
            <Route path="submissions" element={<AdminSubmissionsPage />} />
            <Route path="members" element={<AdminMembersPage />} />
            <Route path="media" element={<AdminMediaPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </QueryClientProvider>
  );
}
